<template lang="pug">
  .input.row.middle.relative(:class='{active: active || search, disabled}')
    simple-svg.m-right-1(:filepath='searchIcon' width='16px' height='16px')
    input.text-input.width-100.text-normal.ellipsis(
      @focus='focusInput'
      @blur='blurInput'
      @keypress.enter='goToSearchResults'
      v-model='search'
      :disabled='disabled'
      :placeholder='$t("feed.searchbar.searchByTextPlaceholder")'
      :class='{active: active || search}'
    )
    .options.absolute(v-if='active')
      .options.loading.kimple-scroll(v-if='showLoader')
        .option-title.row.middle.center
          .spinner
      .options.kimple-scroll(v-if='popularContent && !search')
        .width-100.row.middle.text-normal.bold.option-title {{ $t("feed.searchbar.popularContent") }}
        router-link.search-option.option.row.middle.width-100.ellipsis(
          v-for='content in popularContent.content.slice(0, 4)'
          :to='getContentLink(content)'
          :key='content.id'
          @click.native='blurInput'
        )
          img.content-image(v-if='showContentImage(content)' :src='content.image')
          simple-svg.blue-icon(v-else :filepath='contentIcons[content.type]' width='22px' height='22px')
          .text-normal.ellipsis.m-left-1 {{ content.name }}
        .width-100.row.middle.text-normal.bold.option-title {{ $t("feed.searchbar.popularAuthors") }}
        router-link.search-option.option.row.middle.width-100.ellipsis(
          v-for='author in popularContent.authors.slice(0, 4)'
          :to='getAuthorLink(author)'
          :key='author.id'
          @click.native='blurInput'
        )
          img.author-image(v-if='author.image' :src='author.image')
          simple-svg.m-left-05.blue-icon(v-else :filepath='userIcon' width='18px' height='18px')
          .text-normal.ellipsis.m-left-1 {{ author.name }}
      .options.kimple-scroll(v-if='options && search')
        .empty.row.middle.center.dark-gray.option-title(v-if='!options.length') {{ $t("feed.searchbar.noResult") }}
        router-link.search-option.option.row.middle.width-100.ellipsis(
          v-else
          v-for='content in options'
          :to='getContentLink(content)'
          :key='content.id'
          @click.native='blurInput'
        )
          img.content-image(v-if='showContentImage(content)' :src='content.image')
          simple-svg.blue-icon(v-else :filepath='contentIcons[content.type]' width='22px' height='22px')
          .text-normal.ellipsis.m-left-1(v-html='highlightText(content.name)')
</template>

<script>
import searchIcon from '@/assets/ic_search.svg'
import { mapActions, mapState } from 'vuex'
import debounce from 'lodash.debounce'
import { routes } from '../../router'
import userIcon from '@/assets/ic_user.svg'
import folderIcon from '@/assets/ic_folder.svg'
import resourceIcon from '@/assets/ic_resource.svg'

export default {
  props: {
    disabled: { type: Boolean }
  },
  data() {
    return {
      userIcon,
      folderIcon,
      searchIcon,
      search: '',
      active: false,
      loading: false,
      contentIcons: {
        resources: resourceIcon,
        kimpleFolders: folderIcon
      },
      popularContent: null,
      options: null
    }
  },
  watch: {
    search(value) {
      if (value) this.searchContent()
      else this.searchPopular()
    },
    active(value) {
      if (!value) {
        this.options = null
        this.popularContent = null
      } else {
        if (this.search) this.searchContent()
        else this.searchPopular()
      }
    }
  },
  computed: {
    ...mapState({
      locale: state => state.behavior.locale
    }),
    showLoader() {
      return this.active && this.loading && !this.popularContent && !this.options
    }
  },
  methods: {
    ...mapActions([
      'searchPopularContent',
      'searchContentByText'
    ]),
    showContentImage(content) {
      return content.type === 'educationalPrograms' ||
        (
          content.type === 'resources' && content.image &&
          (
            content.image.endsWith('.png') ||
            content.image.endsWith('.jpg') ||
            content.image.endsWith('.jpeg')
          )
        )
    },
    getContentLink({ type, id }) {
      if (type === 'resources') return { name: routes.resource, params: { id } }
      else if (type === 'kimpleFolders') return { name: routes.kimpleFolderContent, params: { id } }
      else return { name: routes.educationalProgram, params: { id } }
    },
    getAuthorLink({ name, id }) {
      return { name: routes.search, query: { authorId: id, authorName: name } }
    },
    focusInput(evt) {
      this.setActive(evt, true)
    },
    blurInput(evt) {
      this.setActive(evt, false)
    },
    setActive(evt, status) {
      if (
        evt.relatedTarget &&
        Array.from(evt.relatedTarget.classList).includes('search-option') &&
        !status
      ) return
      this.active = status
    },
    highlightText(text) {
      const regex = new RegExp(this.search, 'gi')
      text.replace(/(<span class="highlight">|<\/span>)/gim, '')
      return text.replace(regex, '<span class="text-normal bold">$&</span>')
    },
    goToSearchResults() {
      if (!this.search) return
      this.$router.push({
        name: routes.search,
        query: {
          ...this.$route.query,
          name: this.search
        }
      })
      this.search = ''
      this.options = null
      this.popularContent = null
    },
    searchPopular: debounce(async function() {
      this.loading = true
      const { success, data } = await this.searchPopularContent({ locale: this.locale })
      if (success) {
        this.popularContent = {
          authors: data.authors.slice(0, 4),
          content: [
            ...data.resources.map(res => ({ ...res, type: 'resources' })),
            ...data.educationalPrograms.map(res => ({ ...res, type: 'educationalPrograms' })),
            ...data.kimpleFolders.map(res => ({ ...res, type: 'kimpleFolders' }))
          ].sort((a, b) => b.views - a.views).slice(0, 4)
        }
      }
      this.loading = false
    }, 1000),
    searchContent: debounce(async function() {
      this.loading = true
      if (!this.search) return
      this.options = null
      const { success, data } = await this.searchContentByText({
        search: this.search,
        locale: this.locale
      })
      if (success) {
        delete data.authors
        this.options = Object
          .keys(data)
          .reduce((acc, key) => {
            data[key].forEach(content => {
              acc.push({
                type: key,
                name: content.name,
                id: content.id
              })
            })
            return acc
          }, [])
          .slice(0, 10)
      }
      this.loading = false
    }, 1000)
  }
}
</script>

<style lang="scss" scoped>
@import 'src/scss/variables/colors';

.input{
  width: 150px;
  margin: 0;
  transition: all .5s ease;
  background-color: $white;
  height: 40px;
  border-radius: 5px;
  padding: 0 8px;
  &.disabled{
    opacity: 0.6;
    cursor: not-allowed;
    transition: all 0.5s ease;
  }
  &.active{
    width: 248px;
    transition: all .5s ease;
  }
  .text-input{
    background: none;
  }
  .options{
    background-color: $white;
    width: 320px;
    border-radius: 4px;
    max-height: 360px;
    overflow-y: auto;
    top: calc(100% + 4px);
    left: 0;
    right: 0;
    z-index: 2;
    box-shadow: 0px 2px 2px 0px #73798540;
    .option{
      height: 45px;
      padding: 0 10px;
      &:hover{
        background-color: $light-gray;
      }
      .author-image{
        width: 24px;
        height: 24px;
        border-radius: 50%;
        object-fit: cover;
      }
      .content-image{
        width: 24px;
        height: 24px;
        border-radius: 4px;
        object-fit: cover;
      }
    }
    .option-title{
      height: 40px;
      padding: 0 10px;
    }
  }
}

.spinner{
  width: 20px;
  height: 20px;
  padding: 5px;
  border: 2px solid transparent;
  border-top: 2px solid $gray;
  border-left: 2px solid $gray;
  border-radius: 50%;
  animation: loading 1s linear infinite;
}

@keyframes loading {
  from{
    transform: rotate(0);
  }
  to{
    transform: rotate(360deg);
  }
}
</style>
